import {
  YES_NO,
  applicantSection,
  feeInfo,
  feeProofAttachment,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import {
  ARCHITECTURE_ORGAN,
  PB,
  allOf,
  exemptWhen,
  investorSection,
  localPlanField,
  otherLawsAttachment,
  propertySection,
  rightToDisposeAttachment,
  rightToDisposeSection,
  stampDutyExemptionFields,
  zoningDecisionAttachment,
} from "modules/ebiForms/definitions/sharedVehiclesConstruction";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * F5. Wniosek o pozwolenie na budowę (PB-1) albo na rozbiórkę (PB-3) — art. 28, 32, 33 i art. 30b ustawy Prawo budowlane
 * (t.j. Dz. U. 2025 poz. 418, tekst https://api.sejm.gov.pl/eli/acts/DU/2025/418/text.pdf — pobrany 2026-10-22).
 * Wzory ogólnopolskie GUNB (wersja 2026): PB-1 https://www.gov.pl/attachment/8f612ad2-3511-4397-adfd-bc6fd8e421b9,
 * PB-3 https://www.gov.pl/attachment/680b8152-9122-4fca-8295-4b660c892e92, PB-5
 * https://www.gov.pl/attachment/0bac7f79-cfeb-4ed8-a6c8-2d831cee570c — pobrane 2026-10-22 (DOCX); sekcje: organ (urząd
 * EBI), inwestor, pełnomocnik, rodzaj i nazwa zamierzenia / informacje o rozbiórce, nieruchomość, załączniki. Oświadczenie
 * PB-5 składane w formularzu (pozostali inwestorzy — załącznik). Opłata skarbowa za pozwolenie na budowę zależy od rodzaju
 * obiektu, więc formularz pokazuje stawki, a kwotę potwierdza urząd; za rozbiórkę 36 zł (część III ust. 10 załącznika do
 * ustawy o opłacie skarbowej — według katalogu). Wniosek można złożyć także w e-Budownictwie.
 */
const BUILD: EbiCondition = { field: "rodzajWniosku", equals: "pb1" };
const DEMOLISH: EbiCondition = { field: "rodzajWniosku", equals: "pb3" };
const CHOSEN: EbiCondition = { field: "rodzajWniosku", filled: true };

const F5: IEbiFormDefinition = {
  code: "F5",
  version: 1,
  title: "Wniosek o pozwolenie na budowę albo rozbiórkę",
  category: EEbiFormCategory.Construction,
  office: EEbiFormOffice.County,
  description: "Pozwolenie na budowę (formularz PB-1) albo na rozbiórkę obiektu budowlanego (PB-3).",
  keywords: ["pozwolenie na budowę", "PB-1", "rozbiórka", "PB-3", "budowa", "projekt budowlany", "architektura"],
  organ: ARCHITECTURE_ORGAN,
  legalBasis: `art. 28, art. 32 i art. 33 (pozwolenie na budowę) oraz art. 30b (pozwolenie na rozbiórkę) ${PB}`,
  request: "Wnoszę o wydanie decyzji o pozwoleniu na budowę albo na rozbiórkę obiektu budowlanego opisanego we wniosku.",
  feeLabel: "wg stawek",
  fee: "Pozwolenie na budowę — opłata skarbowa zależna od obiektu: budynek na działalność gospodarczą 1 zł za m² powierzchni użytkowej (najwyżej 539 zł), budynek gospodarczy w gospodarstwie rolnym 14 zł, inny budynek 48 zł, studnie i urządzenia wodne 20 zł, urządzenia budowlane 91 zł, sieci powyżej 1 km 2143 zł (do 1 km 105 zł), inne budowle 155 zł, przebudowa i remont 50% stawki; budownictwo mieszkaniowe zwolnione. Pozwolenie na rozbiórkę — 36 zł. Pełnomocnictwo — 17 zł.",
  deadlineLabel: "do 65 dni",
  deadline: "Pozwolenie na budowę — do 65 dni od dnia złożenia wniosku (art. 35 ust. 6 Prawa budowlanego); rozbiórka — według KPA.",
  variantField: "rodzajWniosku",
  variants: {
    pb1: { title: "Wniosek o pozwolenie na budowę (PB-1)" },
    pb3: { title: "Wniosek o pozwolenie na rozbiórkę (PB-3)" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj wniosku",
      fields: [
        {
          code: "rodzajWniosku",
          type: "radio",
          label: "Wnoszę o",
          required: true,
          options: [
            { value: "pb1", label: "pozwolenie na budowę (PB-1)" },
            { value: "pb3", label: "pozwolenie na rozbiórkę (PB-3)" },
          ],
        },
        feeInfo(
          "infoRodzaj",
          "Rozbiórka niektórych obiektów nie wymaga pozwolenia, a jedynie zgłoszenia (art. 31 Prawa budowlanego). Wniosek możesz złożyć także w portalu e-Budownictwo.",
        ),
      ],
    },
    applicantSection(),
    representationSection(),
    investorSection(),
    {
      code: "zamierzenie",
      title: "Rodzaj i nazwa zamierzenia budowlanego",
      visibleWhen: BUILD,
      fields: [
        {
          code: "rodzajZamierzenia",
          type: "checkboxes",
          label: "Rodzaj zamierzenia budowlanego (wskaż wszystkie właściwe)",
          required: true,
          options: [
            { value: "budowa", label: "budowa nowego obiektu budowlanego" },
            { value: "rozbudowa", label: "rozbudowa istniejącego obiektu budowlanego" },
            { value: "nadbudowa", label: "nadbudowa istniejącego obiektu budowlanego" },
            { value: "odbudowa", label: "odbudowa obiektu budowlanego" },
            { value: "inne", label: "wykonanie robót budowlanych innych niż wymienione" },
          ],
        },
        {
          code: "nazwaZamierzenia",
          type: "text",
          label: "Nazwa zamierzenia budowlanego",
          required: true,
          maxLength: 500,
          hint: "Ogólne określenie zamierzenia, np. „Zespół budynków jednorodzinnych wraz z infrastrukturą”.",
        },
        localPlanField(),
        {
          code: "dsu",
          type: "radio",
          label: "Czy dla przedsięwzięcia wydano decyzję o środowiskowych uwarunkowaniach?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
      ],
    },
    {
      code: "rozbiorka",
      title: "Informacje o rozbiórce",
      visibleWhen: DEMOLISH,
      fields: [
        { code: "nazwaObiektu", type: "text", label: "Nazwa obiektu przeznaczonego do rozbiórki", required: true, maxLength: 500 },
        {
          code: "wlascicielObiektu",
          type: "radio",
          label: "Czy inwestor jest właścicielem obiektu budowlanego?",
          required: true,
          options: YES_NO,
        },
        {
          code: "zabytek",
          type: "radio",
          label: "Czy obiekt jest wpisany do rejestru zabytków?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
      ],
    },
    propertySection("Dane nieruchomości (miejsce wykonywania robót budowlanych albo rozbiórki)"),
    rightToDisposeSection(BUILD),
    {
      code: "oplata",
      title: "Opłata skarbowa",
      visibleWhen: CHOSEN,
      fields: [
        {
          ...feeInfo(
            "infoOplata",
            "Opłata skarbowa za pozwolenie na budowę zależy od rodzaju obiektu (stawki w nagłówku wniosku) — urząd potwierdzi kwotę. Sprawy budownictwa mieszkaniowego są zwolnione z opłaty.",
          ),
          visibleWhen: BUILD,
        },
        ...stampDutyExemptionFields(),
        {
          code: "oplataRozbiorka",
          type: "fee",
          label: "Opłata skarbowa za pozwolenie na rozbiórkę",
          visibleWhen: DEMOLISH,
          feeAmount: 36,
          feeWaivedWhen: exemptWhen(),
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "projekt",
      label: "Projekt zagospodarowania działki lub terenu oraz projekt architektoniczno-budowlany",
      hint: "W postaci elektronicznej (pliki podpisane przez projektanta) — wraz z opiniami, uzgodnieniami i pozwoleniami wymaganymi przepisami odrębnych ustaw (art. 33 ust. 2 pkt 1).",
      multiple: true,
      visibleWhen: BUILD,
      required: true,
    },
    zoningDecisionAttachment(BUILD),
    {
      code: "decyzjaSrodowiskowa",
      label: "Decyzja o środowiskowych uwarunkowaniach wraz z załącznikiem graficznym",
      visibleWhen: allOf(BUILD, { field: "dsu", equals: "tak" }),
      required: true,
    },
    rightToDisposeAttachment(BUILD),
    {
      code: "zgodaWlasciciela",
      label: "Zgoda właściciela obiektu budowlanego",
      hint: "Oryginał albo kopia (art. 30b ust. 3 pkt 1).",
      visibleWhen: allOf(DEMOLISH, { field: "wlascicielObiektu", equals: "nie" }),
      required: true,
    },
    { code: "szkic", label: "Szkic usytuowania obiektu budowlanego", visibleWhen: DEMOLISH, required: true },
    { code: "opisRobot", label: "Opis zakresu i sposobu prowadzenia robót rozbiórkowych", visibleWhen: DEMOLISH, required: true },
    { code: "opisBezpieczenstwa", label: "Opis sposobu zapewnienia bezpieczeństwa ludzi i mienia", visibleWhen: DEMOLISH, required: true },
    { code: "projektRozbiorki", label: "Projekt rozbiórki", hint: "W zależności od potrzeb.", visibleWhen: DEMOLISH },
    {
      code: "decyzjaGkz",
      label: "Decyzja Generalnego Konserwatora Zabytków o skreśleniu obiektu z rejestru zabytków",
      visibleWhen: allOf(DEMOLISH, { field: "zabytek", equals: "tak" }),
      required: true,
    },
    otherLawsAttachment(
      "Np. pozwolenie konserwatorskie, pozwolenie wodnoprawne, zgoda na odstępstwo (art. 9), umowa urbanistyczna, wynik audytu bezpieczeństwa ruchu drogowego; przy rozbiórce — bez uzgodnień z oceny oddziaływania na środowisko lub na obszar Natura 2000.",
      CHOSEN,
    ),
    ...proxyAttachments(),
    feeProofAttachment("Potwierdzenie uiszczenia opłaty skarbowej", { visibleWhen: allOf(CHOSEN, { not: exemptWhen() }) }),
    otherAttachments(),
  ],
};

export default F5;
